import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Search, Trophy, ArrowRight } from "lucide-react";
import { useApiResource } from "../api/useApi";
import { getRankings, listUploads } from "../api/resources";
import type { RankingItem } from "../api/resources";
import { uploadsToSessions } from "../api/adapters";
import { ACTIVITY_LABEL, ACTIVITIES, ActivityId, Session, activityFamily, formatTime } from "../mockData";
import { ActivityFilter, AngleFilter, FilterCard } from "../components/VisualFilter";
import { StatusBadge } from "../components/StatusBadge";

/**
 * Search over LIVE data (owner, 2026-10-13: "make search screen also use live data"): athletes from the rankings
 * (real names, scores and clip counts) and the viewer's own sessions from the uploads list. It used to filter a fixed
 * list of invented "public analyses". The activity, angle, score and text filters apply to both.
 */

/** The camera angle in a technique's label ("Golf — Swing (DTL)" → "DTL"); null when the label names none. */
export function angleOf(activityId: string): string | null {
  const label = ACTIVITIES.find((a) => a.id === activityId)?.label ?? "";
  if (/face-on/i.test(label)) return "Face-on";
  if (/\bDTL\b/.test(label)) return "DTL";
  if (/\bside\b/i.test(label)) return "Side";
  if (/\brear\b|\bbehind\b/i.test(label)) return "Rear";
  return null;
}

export interface SearchFilters {
  activity: string; // a family ("Golf") or "all"
  angle: string; // "Face-on" | "DTL" | "Side" | "Rear" | "all"
  minScore: number;
  maxScore: number;
  q: string;
}

const techniqueMatches = (activityId: string, f: SearchFilters) =>
  (f.activity === "all" || activityFamily(activityId as ActivityId) === f.activity) &&
  (f.angle === "all" || angleOf(activityId) === f.angle);
const scoreMatches = (score: number | null | undefined, f: SearchFilters) => {
  const ranged = f.minScore > 0 || f.maxScore < 100;
  if (score == null) return !ranged; // unscored only when no range is asked for
  return score >= f.minScore && score <= f.maxScore;
};

export function filterAthletes(athletes: RankingItem[], f: SearchFilters): RankingItem[] {
  const q = f.q.trim().toLowerCase();
  return athletes.filter(
    (a) =>
      (f.activity === "all" && f.angle === "all"
        ? true
        : (a.perTechnique ?? []).some((t) => techniqueMatches(t.sportId, f))) &&
      scoreMatches(a.hybridScore ?? a.latestScore ?? null, f) &&
      (!q || (a.name ?? "").toLowerCase().includes(q)),
  );
}

export function filterSessions(sessions: Session[], f: SearchFilters): Session[] {
  const q = f.q.trim().toLowerCase();
  return sessions.filter(
    (s) =>
      techniqueMatches(s.activity, f) &&
      scoreMatches(s.score, f) &&
      (!q ||
        (ACTIVITY_LABEL[s.activity] ?? "").toLowerCase().includes(q) ||
        (s.subjectName ?? "").toLowerCase().includes(q) ||
        (s.notes ?? "").toLowerCase().includes(q) ||
        s.id.includes(q)),
  );
}

const fmtScore = (n: number | null | undefined) => (n == null ? "—" : String(Math.round(n)));

export default function SearchLive() {
  const { user } = useAuth0();
  const [activity, setActivity] = useState("all");
  const [angle, setAngle] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [q, setQ] = useState("");
  const f: SearchFilters = { activity, angle, minScore, maxScore, q };

  const { data: rankings, isLoading: loadingAthletes } = useApiResource((apiFetch) => getRankings(apiFetch, { sort: "score" }), []);
  // The viewer's newest 100 sessions — what a search over "your sessions" reasonably covers.
  const { data: uploads, isLoading: loadingSessions } = useApiResource(
    (apiFetch) => listUploads(apiFetch, { page: 1, pageSize: 100 }),
    [],
  );
  const athletes = useMemo(() => rankings?.values ?? [], [rankings]);
  const sessions = useMemo(
    () => uploadsToSessions(uploads?.values, { athleteName: user?.name ?? user?.email ?? undefined }),
    [uploads, user],
  );
  const foundAthletes = useMemo(() => filterAthletes(athletes, f), [athletes, activity, angle, minScore, maxScore, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const foundSessions = useMemo(() => filterSessions(sessions, f), [sessions, activity, angle, minScore, maxScore, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const top = athletes.filter((a) => a.hybridScore != null).slice(0, 3);

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={(activity !== "all" ? 1 : 0) + (angle !== "all" ? 1 : 0) + (minScore > 0 || maxScore < 100 ? 1 : 0) + (q ? 1 : 0)}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search athletes or your sessions…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
              data-testid="search-input"
            />
          </div>
        }
        summary={[
          { label: "Activity", value: activity === "all" ? "All" : activity, onClear: activity !== "all" ? () => setActivity("all") : undefined },
          { label: "Angle", value: angle === "all" ? "All" : angle, onClear: angle !== "all" ? () => setAngle("all") : undefined },
          {
            label: "Score",
            value: `${minScore} – ${maxScore}`,
            onClear: minScore > 0 || maxScore < 100 ? () => { setMinScore(0); setMaxScore(100); } : undefined,
          },
        ]}
      >
        <ActivityFilter value={activity} onChange={setActivity} />
        <AngleFilter value={angle} onChange={setAngle} />
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span className="w-16 shrink-0 font-medium">Score</span>
          <input type="number" min={0} max={100} value={minScore} onChange={(e) => setMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            className="w-16 h-8 rounded-md border border-slate-200 px-2" aria-label="Minimum score" />
          <span>to</span>
          <input type="number" min={0} max={100} value={maxScore} onChange={(e) => setMaxScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            className="w-16 h-8 rounded-md border border-slate-200 px-2" aria-label="Maximum score" />
        </div>
      </FilterCard>

      {top.length > 0 && !q && activity === "all" && angle === "all" && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Top athletes</div>
            <div className="ml-auto text-[0.7rem] text-slate-500">From the rankings</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {top.map((a) => (
              <Link key={a.contactId} to={`/athletes/${a.contactId}`} className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                <div className="text-xs text-slate-500">#{a.rank}</div>
                <div className="text-sm font-semibold text-slate-900 truncate">{a.name ?? "Unnamed athlete"}</div>
                <div className="text-xs text-slate-500">
                  Score {fmtScore(a.hybridScore)} · {a.clipCount} {a.clipCount === 1 ? "clip" : "clips"}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="card p-4" data-testid="search-athletes">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-900">Athletes</div>
          <div className="text-xs text-slate-500">
            {loadingAthletes ? "Loading…" : `${foundAthletes.length} of ${athletes.length}`}
          </div>
        </div>
        {!loadingAthletes && foundAthletes.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">No athletes match your search.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {foundAthletes.slice(0, 50).map((a) => (
              <li key={a.contactId}>
                <Link to={`/athletes/${a.contactId}`} className="flex items-center gap-3 py-2 px-1 hover:bg-slate-50 rounded">
                  <span className="w-8 text-xs text-slate-400 tabular-nums">#{a.rank}</span>
                  <span className="flex-1 min-w-0 truncate text-sm text-slate-900">{a.name ?? "Unnamed athlete"}</span>
                  <span className="text-xs text-slate-500">{a.clipCount} {a.clipCount === 1 ? "clip" : "clips"}</span>
                  <span className="w-10 text-right text-sm font-semibold text-el-700 tabular-nums">{fmtScore(a.hybridScore)}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card p-4" data-testid="search-sessions">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-900">Your sessions</div>
          <div className="text-xs text-slate-500">
            {loadingSessions ? "Loading…" : `${foundSessions.length} of your newest ${sessions.length}`}
          </div>
        </div>
        {!loadingSessions && foundSessions.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">None of your sessions match.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {foundSessions.slice(0, 50).map((s) => (
              <li key={s.id}>
                <Link to={`/sessions/${s.id}`} className="flex items-center gap-3 py-2 px-1 hover:bg-slate-50 rounded">
                  <span className="text-lg">{s.thumb}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm text-slate-900">{ACTIVITY_LABEL[s.activity] ?? "Session"}</span>
                    <span className="block truncate text-[0.7rem] text-slate-500">
                      #{s.id} · {formatTime(s.capturedAt)}{s.subjectName ? ` · ${s.subjectName}` : ""}
                    </span>
                  </span>
                  <span className="w-10 text-right text-sm font-semibold text-el-700 tabular-nums">{fmtScore(s.score)}</span>
                  <StatusBadge status={s.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
